/**
 * Agent Capabilities — derived from stepRegistry.ts (single source of truth).
 *
 * AGENT_FOR_FIELD and AGENT_EDITABLE_FIELDS are computed from STEPS so they
 * never drift from the step registry. Do NOT hand-code these mappings here.
 */
import { STEPS } from './stepRegistry';

/** Map from field name → owning agent, derived from stepRegistry.STEPS. */
export const AGENT_FOR_FIELD: Record<string, string> = {};
/** Map from agent name → fields it can write, derived from stepRegistry.STEPS. */
export const AGENT_EDITABLE_FIELDS: Record<string, string[]> = {};

for (const step of STEPS) {
  for (const field of step.primaryFields) {
    AGENT_FOR_FIELD[field] = step.cfaAgent;
    if (!AGENT_EDITABLE_FIELDS[step.cfaAgent]) {
      AGENT_EDITABLE_FIELDS[step.cfaAgent] = [];
    }
    if (!AGENT_EDITABLE_FIELDS[step.cfaAgent].includes(field)) {
      AGENT_EDITABLE_FIELDS[step.cfaAgent].push(field);
    }
  }
}

/** Mirrors AGENT_PROMPTS in backend/app/services/sp_agent_registry.py */
export const AGENT_SYSTEM_PROMPT_FILE: Record<string, string> = {
  problem_statement:   'problem_statement_cfa_system_prompt.txt',
  solution_desc:       'solution_description_cfa_system_prompt.txt',
  claims:              'claims_cfa_system_prompt.txt',
  fto:                 'fto_cfa_system_prompt.txt',
  patentability:       'patentability_cfa_system_prompt.txt',
  patent_application:  'patent_application_cfa_system_prompt.txt',
  industrialization:   'industrialization_cfa_system_prompt.txt',
  sota:                'sota_cfa_system_prompt.txt',
};

import { AGENT_TASK_PROMPTS, type PromptEntry } from './promptsCatalog';
export type { PromptEntry };

export interface ToolDef {
  name: string;
  descriptionKey: string;
  available: boolean;
  unavailableReasonKey?: string;
}

export interface KBRef {
  id: string;
  name: string;
  type: 'main' | 'context';
}

export interface AgentCapabilities {
  agentName: string;
  systemPromptFile: string;
  taskPrompts: PromptEntry[];
  tools: ToolDef[];
  writableFields: string[];
  kbs: KBRef[];
}

/**
 * Pilot orchestrator capabilities. The Pilot guides the SP workflow and opens
 * step workspaces; it does not write field content directly (CFAs do that).
 * Tools mirror get_pilot_tools() in backend/app/services/pilot_tool_executor.py.
 */
export function getPilotCapabilities(kbId?: string, kbName?: string): AgentCapabilities {
  const tools: ToolDef[] = [
    { name: 'list_steps', descriptionKey: 'scv2.pilot.tools.listSteps', available: true },
    { name: 'read_step_outcome', descriptionKey: 'scv2.pilot.tools.readStepOutcome', available: true },
    { name: 'open_step', descriptionKey: 'scv2.pilot.tools.openStep', available: true },
    { name: 'read_sp_field', descriptionKey: 'scv2.pilot.tools.readSpField', available: true },
    { name: 'mark_step_status', descriptionKey: 'scv2.pilot.tools.markStepStatus', available: true },
  ];

  const kbs: KBRef[] = [];
  if (kbId && kbName) {
    kbs.push({ id: kbId, name: kbName, type: 'main' });
  }

  return {
    agentName: 'pilot',
    systemPromptFile: 'pilot_system_prompt.txt',
    taskPrompts: [],
    tools,
    writableFields: [],
    kbs,
  };
}

export function getAgentCapabilities(
  activeField: string | null,
  _entityType: string,
  canEdit: boolean,
  kbId?: string,
  kbName?: string,
): AgentCapabilities {
  const agentName = AGENT_FOR_FIELD[activeField ?? ''] ?? 'problem_statement';
  const systemPromptFile = AGENT_SYSTEM_PROMPT_FILE[agentName] ?? '';
  const writableFields = canEdit ? (AGENT_EDITABLE_FIELDS[agentName] ?? []) : [];

  const kbs: KBRef[] = [];
  if (kbId && kbName) {
    kbs.push({ id: kbId, name: kbName, type: 'main' });
  }

  // tools: [] — CFA tools are fetched live from /api/agent/tools by AgentInfoPanel.
  // Only the Pilot (getPilotCapabilities) keeps a local tool list.
  return { agentName, systemPromptFile, taskPrompts: AGENT_TASK_PROMPTS[agentName] ?? [], tools: [], writableFields, kbs };
}
